import React, { useState, useEffect } from 'react';
import { LeaveRequest, attendanceApi, LeaveType } from '../services/attendanceApi';
import LeaveRequestModal from '../components/LeaveRequestModal';
import CancelRequestModal from '../components/CancelRequestModal';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';

const AttendanceManagement: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useAlert();
  const [leaveBalance, setLeaveBalance] = useState<{ total: number; used: number; remaining: number }>({
    total: 0,
    used: 0,
    remaining: 0
  });
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      fetchData();
    }
  }, [user?.id]);

  const fetchData = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);

      // 현재 직원의 휴가 잔액 가져오기
      const balanceData = await attendanceApi.getLeaveBalance(user.id);
      if (balanceData) {
        setLeaveBalance({
          total: typeof balanceData.total === 'string' ? parseFloat(balanceData.total) : balanceData.total,
          used: typeof balanceData.used === 'string' ? parseFloat(balanceData.used) : balanceData.used,
          remaining: typeof balanceData.remaining === 'string' ? parseFloat(balanceData.remaining) : balanceData.remaining
        });
      } else {
        setLeaveBalance({ total: 0, used: 0, remaining: 0 });
      }

      // 현재 직원의 휴가 신청 내역 가져오기
      const requestsData = await attendanceApi.getLeaveRequestsByEmployee(user.id);
      setLeaveRequests(requestsData);
    } catch (error) {
      showToast('근태 데이터를 불러오는데 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<LeaveRequest | undefined>();
  const [editingRequest, setEditingRequest] = useState<LeaveRequest | undefined>();

  const getStatusStyle = (status: string) => {
    const styles = {
      '승인': 'bg-green-100 text-green-800',
      '상신중': 'bg-yellow-100 text-yellow-800',
      '반려': 'bg-red-100 text-red-800',
      '취소 요청 중': 'bg-orange-100 text-orange-800',
      '취소됨': 'bg-gray-100 text-gray-800'
    };
    return styles[status as keyof typeof styles] || 'bg-slate-100 text-slate-800';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR');
  };

  const handleNewRequest = async (newRequest: Omit<LeaveRequest, 'id' | 'requestDate' | 'status' | 'employee' | 'createdAt' | 'updatedAt'>) => {
    if (!user?.id) {
      showToast('로그인 정보를 찾을 수 없습니다.', 'error');
      return;
    }
    
    try {
      if (editingRequest) {
        // 수정 모드
        await attendanceApi.updateLeaveRequest(editingRequest.id, {
          type: newRequest.type as LeaveType,
          startDate: newRequest.startDate,
          endDate: newRequest.endDate,
          days: newRequest.days,
          reason: newRequest.reason
        });
        setEditingRequest(undefined);
      } else {
        // 새 신청 모드
        await attendanceApi.createLeaveRequest({
          type: newRequest.type as LeaveType,
          startDate: newRequest.startDate,
          endDate: newRequest.endDate,
          days: newRequest.days,
          reason: newRequest.reason,
          employeeId: user.id
        });
      }
      
      // 데이터 새로고침
      await fetchData();
      setIsRequestModalOpen(false);
    } catch (error) {
      showToast('휴가 신청 처리 중 오류가 발생했습니다.', 'error');
    }
  };

  const handleCancelRequest = async (requestId: number, cancelReason?: string) => {
    if (!user?.id) {
      showToast('로그인 정보를 찾을 수 없습니다.', 'error');
      return;
    }

    const request = leaveRequests.find(r => r.id === requestId);
    if (!request) {
      showToast('요청을 찾을 수 없습니다.', 'error');
      return;
    }

    try {
      if (request.status === '승인') {
        // 승인된 휴가 - 취소 요청 생성 (사유 필수)
        if (!cancelReason?.trim()) {
          showToast('취소 사유를 입력해주세요.', 'warning');
          return;
        }

        await attendanceApi.createCancelRequest(requestId, {
          cancelReason: cancelReason.trim(),
          employeeId: user.id
        });
        showToast('취소 요청이 제출되었습니다. 관리자 승인을 기다려주세요.', 'success');
      } else {
        // 상신중, 반려 상태 - 즉시 삭제
        await attendanceApi.deleteLeaveRequest(requestId);
        showToast('휴가 신청이 취소되었습니다.', 'success');
      }

      await fetchData(); // 데이터 새로고침
      setIsCancelModalOpen(false);
      setSelectedRequest(undefined);
    } catch (error) {
      console.error('취소 처리 중 오류:', error);
      showToast('취소 처리 중 오류가 발생했습니다.', 'error');
    }
  };

  const openCancelModal = (request: LeaveRequest) => {
    setSelectedRequest(request);
    setIsCancelModalOpen(true);
  };

  const openEditModal = (request: LeaveRequest) => {
    setEditingRequest(request);
    setIsRequestModalOpen(true);
  };

  const handleCloseRequestModal = () => {
    setIsRequestModalOpen(false);
    setEditingRequest(undefined);
  };

  return (
    <>
      <main className="flex-1 overflow-y-auto p-6 lg:p-8">
        <header className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">근태 관리</h1>
            <p className="text-slate-500 mt-1">근태 신청 및 취소를 관리합니다.</p>
          </div>
          <button 
            onClick={() => setIsRequestModalOpen(true)}
            className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
          >
            + 휴가 신청
          </button>
        </header>

        {/* 연차 현황 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">발생</p>
                <p className="text-2xl font-bold text-slate-900">{leaveBalance.total}일</p>
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">사용</p>
                <p className="text-2xl font-bold text-slate-900">{leaveBalance.used}일</p>
              </div>
              <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <svg className="h-6 w-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12H6" />
                </svg>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">잔여</p>
                <p className="text-2xl font-bold text-slate-900">{leaveBalance.remaining}일</p>
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* 휴가 신청 내역 */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-800">휴가 신청 내역</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                <tr>
                  <th className="px-6 py-3 font-medium text-left">구분</th>
                  <th className="px-6 py-3 font-medium text-left">시작일</th>
                  <th className="px-6 py-3 font-medium text-left">종료일</th>
                  <th className="px-6 py-3 font-medium text-center">일수</th>
                  <th className="px-6 py-3 font-medium text-left">사유</th>
                  <th className="px-6 py-3 font-medium text-center">상태</th>
                  <th className="px-6 py-3 font-medium text-left">신청일</th>
                  <th className="px-6 py-3 font-medium text-center">액션</th>
                </tr>
              </thead>
              <tbody className="text-slate-700">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-8 text-center text-slate-500">
                      로딩 중...
                    </td>
                  </tr>
                ) : leaveRequests.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-8 text-center text-slate-500">
                      휴가 신청 내역이 없습니다.
                    </td>
                  </tr>
                ) : (
                  leaveRequests.map((request) => (
                    <tr
                      key={request.id}
                      className="border-t border-slate-200 hover:bg-slate-50"
                    >
                      <td className="px-6 py-4 font-medium">{request.type}</td>
                      <td className="px-6 py-4">{formatDate(request.startDate)}</td>
                      <td className="px-6 py-4">{formatDate(request.endDate)}</td>
                      <td className="px-6 py-4 text-center">{request.days}일</td>
                      <td className="px-6 py-4 max-w-xs truncate">{request.reason}</td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusStyle(request.status)}`}>
                          {request.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">{formatDate(request.requestDate)}</td>
                      <td className="px-6 py-4 text-center">
                        {(request.status === '취소 요청 중' || request.status === '취소됨') ? (
                          <span className="text-xs text-slate-500">
                            {request.status === '취소 요청 중' ? '취소 승인 대기 중' : '취소 완료'}
                          </span>
                        ) : (
                          <div className="space-x-2">
                            <button
                              onClick={() => openEditModal(request)}
                              className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                              disabled={request.status === '승인'}
                            >
                              수정
                            </button>
                            <button
                              onClick={() => openCancelModal(request)}
                              className="text-red-600 hover:text-red-900 text-sm font-medium"
                            >
                              {request.status === '승인' ? '취소 요청' : '취소'}
                            </button>
                          </div>
                        )}
                        {request.status === '반려' && request.rejectReason && (
                          <div className="mt-1">
                            <span className="text-xs text-red-600">
                              반려사유: {request.rejectReason}
                            </span>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <LeaveRequestModal
        isOpen={isRequestModalOpen}
        onClose={handleCloseRequestModal}
        onSave={handleNewRequest}
        editingRequest={editingRequest}
      />

      <CancelRequestModal
        isOpen={isCancelModalOpen}
        onClose={() => {
          setIsCancelModalOpen(false);
          setSelectedRequest(undefined);
        }}
        onConfirm={handleCancelRequest}
        request={selectedRequest}
      />
    </>
  );
};

export default AttendanceManagement;